import React, { useState, useEffect } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Plus,
  Trash2,
  Save,
  Upload,
  Star,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFirestoreCollection } from '../../hooks/useFirestoreCollection';
import { useFirestoreDoc } from '../../hooks/useFirestoreDoc';
import { useStorage } from '../../hooks/useStorage';
import { FirebaseImage } from '../FirebaseImage';
import {
  addDocument,
  updateDocument,
  deleteDocument,
} from '../../lib/firestore-helpers';
import type {
  HeroSlide,
  Testimonial,
  TireBrand,
  SiteSettings,
} from '../../types/firebase';

const LANGS = ['fr', 'de', 'en', 'pt'] as const;
const LANG_LABELS: Record<string, string> = { fr: 'Francais', de: 'Deutsch', en: 'English', pt: 'Portugues' };

// ---------------------------------------------------------------------------
// Sub-tabs
// ---------------------------------------------------------------------------

type ContentSubTab = 'hero' | 'testimonials' | 'brands' | 'settings';

const SUB_TABS: { id: ContentSubTab; label: string }[] = [
  { id: 'hero', label: 'Hero Carousel' },
  { id: 'testimonials', label: 'Temoignages' },
  { id: 'brands', label: 'Marques pneus' },
  { id: 'settings', label: 'Parametres site' },
];

// ---------------------------------------------------------------------------
// Hero sub-tab
// ---------------------------------------------------------------------------

const HeroSubTab: React.FC = () => {
  const { data: slides, refetch } = useFirestoreCollection<HeroSlide>({
    collection: 'hero-slides',
    orderBy: ['order', 'asc'],
    realtime: true,
  });
  const { uploadFile, uploading, progress } = useStorage();
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const handleTogglePublished = async (slide: HeroSlide) => {
    try {
      await updateDocument('hero-slides', slide.id, { published: !slide.published });
      toast.success('Statut mis a jour');
    } catch {
      toast.error('Erreur mise a jour');
    }
  };

  const handleReorder = async (slide: HeroSlide, direction: 'up' | 'down') => {
    const sorted = [...slides].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === slide.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    try {
      await updateDocument('hero-slides', sorted[idx].id, { order: sorted[swapIdx].order });
      await updateDocument('hero-slides', sorted[swapIdx].id, { order: sorted[idx].order });
      toast.success('Ordre mis a jour');
    } catch {
      toast.error('Erreur reordonnement');
    }
  };

  const handleChangeImage = async (slide: HeroSlide, file: File) => {
    setUploadingId(slide.id);
    try {
      const path = `hero/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { url } = await uploadFile(file, path);
      await updateDocument('hero-slides', slide.id, { imageUrl: url });
      toast.success('Image mise a jour');
    } catch {
      toast.error('Erreur upload image');
    }
    setUploadingId(null);
  };

  return (
    <div className="space-y-4">
      {slides.length === 0 && (
        <p className="text-gray-500 text-center py-8">Aucune slide configuree</p>
      )}
      {[...slides].sort((a, b) => a.order - b.order).map((slide) => (
        <div key={slide.id} className="bg-white rounded-xl shadow p-4 flex items-center gap-4">
          <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
            <FirebaseImage src={slide.imageUrl} alt={slide.key} className="w-full h-full object-cover" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900">{slide.key}</p>
            <p className="text-sm text-gray-500">Ordre: {slide.order}</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Change image */}
            <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 rounded-lg p-2 transition-colors">
              <Upload className="w-4 h-4 text-gray-600" />
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleChangeImage(slide, f);
                  e.target.value = '';
                }}
              />
            </label>

            {/* Toggle published */}
            <button
              onClick={() => handleTogglePublished(slide)}
              className={`rounded-lg p-2 transition-colors ${
                slide.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
              }`}
              title={slide.published ? 'Publie' : 'Masque'}
            >
              {slide.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </button>

            {/* Reorder */}
            <button
              onClick={() => handleReorder(slide, 'up')}
              className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2 transition-colors"
            >
              <ChevronUp className="w-4 h-4 text-gray-600" />
            </button>
            <button
              onClick={() => handleReorder(slide, 'down')}
              className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2 transition-colors"
            >
              <ChevronDown className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {uploading && uploadingId === slide.id && (
            <div className="w-20">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div className="bg-red-600 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Testimonials sub-tab
// ---------------------------------------------------------------------------

interface TestimonialForm {
  name: string;
  vehicle: string;
  rating: number;
  text: Record<string, string>;
  published: boolean;
  order: number;
}

const emptyTestimonialForm = (): TestimonialForm => ({
  name: '',
  vehicle: '',
  rating: 5,
  text: { fr: '', de: '', en: '', pt: '' },
  published: true,
  order: 0,
});

const TestimonialsSubTab: React.FC = () => {
  const { data: testimonials } = useFirestoreCollection<Testimonial>({
    collection: 'testimonials',
    orderBy: ['order', 'asc'],
    realtime: true,
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<TestimonialForm>(emptyTestimonialForm());
  const [showAdd, setShowAdd] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const startEdit = (t: Testimonial) => {
    setEditing(t.id);
    setShowAdd(false);
    setForm({
      name: t.name,
      vehicle: t.vehicle,
      rating: t.rating,
      text: { fr: t.text?.fr || '', de: t.text?.de || '', en: t.text?.en || '', pt: t.text?.pt || '' },
      published: t.published,
      order: t.order,
    });
  };

  const startAdd = () => {
    setEditing(null);
    setForm({
      ...emptyTestimonialForm(),
      order: testimonials.length > 0 ? Math.max(...testimonials.map((t) => t.order)) + 1 : 0,
    });
    setShowAdd(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Le nom est obligatoire');
      return;
    }

    try {
      if (editing) {
        await updateDocument('testimonials', editing, form);
        toast.success('Temoignage mis a jour');
        setEditing(null);
      } else {
        await addDocument('testimonials', form);
        toast.success('Temoignage ajoute');
        setShowAdd(false);
      }
    } catch {
      toast.error('Erreur sauvegarde');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument('testimonials', id);
      toast.success('Temoignage supprime');
      setDeleteConfirm(null);
      if (editing === id) setEditing(null);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleTogglePublished = async (t: Testimonial) => {
    try {
      await updateDocument('testimonials', t.id, { published: !t.published });
    } catch {
      toast.error('Erreur mise a jour');
    }
  };

  const handleReorder = async (t: Testimonial, direction: 'up' | 'down') => {
    const sorted = [...testimonials].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === t.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    try {
      await updateDocument('testimonials', sorted[idx].id, { order: sorted[swapIdx].order });
      await updateDocument('testimonials', sorted[swapIdx].id, { order: sorted[idx].order });
    } catch {
      toast.error('Erreur reordonnement');
    }
  };

  const renderForm = () => (
    <div className="bg-gray-50 rounded-xl p-4 space-y-4 border border-gray-200">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vehicule</label>
          <input
            value={form.vehicle}
            onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setForm({ ...form, rating: n })}
              className={`p-1 ${n <= form.rating ? 'text-yellow-500' : 'text-gray-300'}`}
            >
              <Star className="w-5 h-5" fill={n <= form.rating ? 'currentColor' : 'none'} />
            </button>
          ))}
        </div>
      </div>

      {LANGS.map((lang) => (
        <div key={lang}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Texte ({LANG_LABELS[lang]})
          </label>
          <textarea
            value={form.text[lang] || ''}
            onChange={(e) => setForm({ ...form, text: { ...form.text, [lang]: e.target.value } })}
            rows={2}
            className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none resize-y"
          />
        </div>
      ))}

      <div className="flex justify-end gap-2">
        <button
          onClick={() => { setEditing(null); setShowAdd(false); }}
          className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Annuler
        </button>
        <button
          onClick={handleSave}
          className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-1"
        >
          <Save className="w-4 h-4" /> Sauvegarder
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={startAdd}
          className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {showAdd && renderForm()}

      {[...testimonials].sort((a, b) => a.order - b.order).map((t) => (
        <div key={t.id}>
          <div className="bg-white rounded-xl shadow p-4 flex items-start gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-medium text-gray-900">{t.name}</p>
                <span className="text-xs text-gray-500">-- {t.vehicle}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-3 h-3 ${n <= t.rating ? 'text-yellow-500' : 'text-gray-300'}`}
                      fill={n <= t.rating ? 'currentColor' : 'none'}
                    />
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{t.text?.fr || t.text?.en || '--'}</p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => handleTogglePublished(t)}
                className={`rounded-lg p-2 transition-colors ${
                  t.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'
                }`}
              >
                {t.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => handleReorder(t, 'up')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronUp className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => handleReorder(t, 'down')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => startEdit(t)} className="bg-blue-100 hover:bg-blue-200 rounded-lg p-2 text-blue-700">
                <Save className="w-4 h-4" />
              </button>
              <button
                onClick={() => setDeleteConfirm(t.id)}
                className="bg-red-100 hover:bg-red-200 rounded-lg p-2 text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {deleteConfirm === t.id && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 flex items-center justify-between">
              <span className="text-sm text-red-700">Confirmer la suppression ?</span>
              <div className="flex gap-2">
                <button onClick={() => handleDelete(t.id)} className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700">
                  Supprimer
                </button>
                <button onClick={() => setDeleteConfirm(null)} className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-300">
                  Annuler
                </button>
              </div>
            </div>
          )}

          {editing === t.id && <div className="mt-2">{renderForm()}</div>}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Brands sub-tab
// ---------------------------------------------------------------------------

const BrandsSubTab: React.FC = () => {
  const { data: brands } = useFirestoreCollection<TireBrand>({
    collection: 'tire-brands',
    orderBy: ['order', 'asc'],
    realtime: true,
  });
  const { uploadFile, uploading, progress } = useStorage();

  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newFile, setNewFile] = useState<File | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!newName.trim() || !newFile) {
      toast.error('Nom et logo obligatoires');
      return;
    }
    try {
      const path = `brands/${Date.now()}_${newFile.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
      const { url } = await uploadFile(newFile, path);
      const order = brands.length > 0 ? Math.max(...brands.map((b) => b.order)) + 1 : 0;
      await addDocument('tire-brands', { name: newName, logoUrl: url, published: true, order });
      toast.success('Marque ajoutee');
      setShowAdd(false);
      setNewName('');
      setNewFile(null);
    } catch {
      toast.error('Erreur ajout marque');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteDocument('tire-brands', id);
      toast.success('Marque supprimee');
      setDeleteConfirm(null);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const handleTogglePublished = async (b: TireBrand) => {
    try {
      await updateDocument('tire-brands', b.id, { published: !b.published });
    } catch {
      toast.error('Erreur mise a jour');
    }
  };

  const handleReorder = async (brand: TireBrand, direction: 'up' | 'down') => {
    const sorted = [...brands].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((b) => b.id === brand.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    try {
      await updateDocument('tire-brands', sorted[idx].id, { order: sorted[swapIdx].order });
      await updateDocument('tire-brands', sorted[swapIdx].id, { order: sorted[idx].order });
    } catch {
      toast.error('Erreur reordonnement');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => setShowAdd(true)}
          className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
        >
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {showAdd && (
        <div className="bg-gray-50 rounded-xl p-4 space-y-4 border border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la marque</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => setNewFile(e.target.files?.[0] || null)}
              className="text-sm"
            />
          </div>
          {uploading && (
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div className="bg-red-600 h-1.5 rounded-full" style={{ width: `${progress}%` }} />
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowAdd(false); setNewName(''); setNewFile(null); }} className="px-4 py-2 text-sm bg-gray-200 rounded-lg hover:bg-gray-300">
              Annuler
            </button>
            <button onClick={handleAdd} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1">
              <Save className="w-4 h-4" /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {[...brands].sort((a, b) => a.order - b.order).map((brand) => (
        <div key={brand.id}>
          <div className="bg-white rounded-xl shadow p-4 flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
              <FirebaseImage src={brand.logoUrl} alt={brand.name} className="w-full h-full object-contain" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-gray-900">{brand.name}</p>
              <p className="text-sm text-gray-500">Ordre: {brand.order}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleTogglePublished(brand)}
                className={`rounded-lg p-2 ${brand.published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}
              >
                {brand.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => handleReorder(brand, 'up')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronUp className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => handleReorder(brand, 'down')} className="bg-gray-100 hover:bg-gray-200 rounded-lg p-2">
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>
              <button onClick={() => setDeleteConfirm(brand.id)} className="bg-red-100 hover:bg-red-200 rounded-lg p-2 text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {deleteConfirm === brand.id && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2 flex items-center justify-between">
              <span className="text-sm text-red-700">Confirmer la suppression de "{brand.name}" ?</span>
              <div className="flex gap-2">
                <button onClick={() => handleDelete(brand.id)} className="bg-red-600 text-white text-xs px-3 py-1 rounded hover:bg-red-700">
                  Supprimer
                </button>
                <button onClick={() => setDeleteConfirm(null)} className="bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded hover:bg-gray-300">
                  Annuler
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Settings sub-tab
// ---------------------------------------------------------------------------

const SettingsSubTab: React.FC = () => {
  const { data: settings, loading, updateDoc } = useFirestoreDoc<SiteSettings>({
    collection: 'site-settings',
    id: 'general',
    realtime: true,
  });

  const [form, setForm] = useState({
    yearsExperience: '',
    vehiclesServed: '',
    satisfaction: '',
    phone: '',
    email: '',
    address: '',
  });

  useEffect(() => {
    if (settings) {
      setForm({
        yearsExperience: settings.stats?.yearsExperience || '',
        vehiclesServed: settings.stats?.vehiclesServed || '',
        satisfaction: settings.stats?.satisfaction || '',
        phone: settings.contact?.phone || '',
        email: settings.contact?.email || '',
        address: settings.contact?.address || '',
      });
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await updateDoc({
        stats: {
          yearsExperience: form.yearsExperience,
          vehiclesServed: form.vehiclesServed,
          satisfaction: form.satisfaction,
        },
        contact: {
          phone: form.phone,
          email: form.email,
          address: form.address,
        },
      } as Partial<SiteSettings>);
      toast.success('Parametres sauvegardes');
    } catch {
      toast.error('Erreur sauvegarde');
    }
  };

  if (loading) {
    return <p className="text-gray-500 text-center py-8">Chargement...</p>;
  }

  return (
    <div className="bg-gray-50 rounded-xl p-6 space-y-6 border border-gray-200">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Statistiques affichees</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Annees d'experience</label>
            <input
              value={form.yearsExperience}
              onChange={(e) => setForm({ ...form, yearsExperience: e.target.value })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Vehicules traites</label>
            <input
              value={form.vehiclesServed}
              onChange={(e) => setForm({ ...form, vehiclesServed: e.target.value })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Satisfaction (%)</label>
            <input
              value={form.satisfaction}
              onChange={(e) => setForm({ ...form, satisfaction: e.target.value })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Coordonnees</h4>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm text-gray-700 mb-1">Telephone</label>
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Email</label>
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Adresse</label>
            <textarea
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={2}
              className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none resize-y"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
        >
          <Save className="w-4 h-4" /> Sauvegarder
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main ContentTab
// ---------------------------------------------------------------------------

const ContentTab: React.FC = () => {
  const [subTab, setSubTab] = useState<ContentSubTab>('hero');

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Gestion du contenu</h2>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-0">
        {SUB_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setSubTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              subTab === tab.id
                ? 'bg-white border border-b-white border-gray-200 text-red-600 -mb-px'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'hero' && <HeroSubTab />}
      {subTab === 'testimonials' && <TestimonialsSubTab />}
      {subTab === 'brands' && <BrandsSubTab />}
      {subTab === 'settings' && <SettingsSubTab />}
    </div>
  );
};

export default ContentTab;
